import React, { useState, useEffect, useCallback } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';

/* ══════════════════════════════════════════════════════════════════
   ÖĞRETMEN PLANLAMA SAYFASI
   - Her öğretmenin hangi gün gelemeyeceğini, haftalık kaç saat
     derse gireceğini gör/düzenle
   - Buna göre ders programı planla
══════════════════════════════════════════════════════════════════ */

const GUNLER = ['Pazartesi','Sali','Carsamba','Persembe','Cuma','Cumartesi','Pazar'];
const GUN_KISALT = { Pazartesi:'Pzt', Sali:'Sal', Carsamba:'Çar', Persembe:'Per', Cuma:'Cum', Cumartesi:'Cmt', Pazar:'Paz' };

export default function OgretmenPlanlamaPage() {
  const [ogretmenler, setOgretmenler] = useState([]);
  const [musaitData, setMusaitData]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [secOgretmen, setSecOgretmen] = useState(null);
  const [editData, setEditData]       = useState([]);
  const [saving, setSaving]           = useState(false);
  const [tab, setTab]                 = useState('gorsel'); // gorsel | liste

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, mRes] = await Promise.all([
        axios.get('/ogretmenler-listesi'),
        axios.get('/admin/ogretmen-musait').catch(() => ({ data: { success: false, data: [] } })),
      ]);
      if (oRes.data.success) setOgretmenler(oRes.data.data || []);
      if (mRes.data.success) setMusaitData(mRes.data.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (ogretmen) => {
    setSecOgretmen(ogretmen);
    const mevcut = musaitData.filter(m => m.id === ogretmen.id);
    const defaultData = GUNLER.map(gun => {
      const m = mevcut.find(x => x.gun === gun);
      return { gun, musait: m ? m.musait === 1 : true, haftalik_saat: m?.haftalik_saat || 0, notlar: m?.notlar || '' };
    });
    setEditData(defaultData);
  };

  const handleSave = async () => {
    if (!secOgretmen) return;
    setSaving(true);
    try {
      // Admin endpoint: POST /admin/ogretmen-musait/:ogretmen_user_id
      await axios.post(`/admin/ogretmen-musait-set`, { ogretmen_id: secOgretmen.id, gunler: editData });
      toast.success(`${secOgretmen.ad} ${secOgretmen.soyad} — Müsaitlik kaydedildi!`);
      setSecOgretmen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Kayıt hatası');
    }
    finally { setSaving(false); }
  };

  // Öğretmenin rengi (brans bazlı)
  const BRANS_COLORS = {
    Matematik: '#2563EB', Fizik: '#7C3AED', Kimya: '#059669', Biyoloji: '#10B981',
    Türkçe: '#E8334A', Edebiyat: '#F59E0B', Tarih: '#D97706', Coğrafya: '#0891B2',
    İngilizce: '#0EA5E9', Almanca: '#8B5CF6', default: '#64748B',
  };
  const getBransColor = (brans) => BRANS_COLORS[brans] || BRANS_COLORS.default;

  // Görsel özet: hangi gün kim müsait
  const gunMusaitMap = {};
  GUNLER.forEach(g => {
    gunMusaitMap[g] = ogretmenler.map(ogr => {
      const m = musaitData.find(md => md.id === ogr.id && md.gun === g);
      return { ...ogr, musait: m ? m.musait === 1 : true, notlar: m?.notlar || '' };
    });
  });

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.4rem' }}>📋 Öğretmen Müsaitlik & Planlama</h2>
        <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginTop:4 }}>
          Öğretmenlerin hangi günler gelebileceğini ve haftalık saat kapasitesini yönetin
        </p>
      </div>

      {/* Tab */}
      <div style={{ display:'flex', gap:8 }}>
        {[{k:'gorsel',l:'📅 Haftalık Görünüm'},{k:'liste',l:'📋 Liste Görünümü'}].map(t => (
          <button key={t.k} className="btn" style={{ background: tab===t.k ? 'var(--blue)' : 'var(--bg-elevated)', color: tab===t.k ? 'white' : 'var(--text-secondary)', border:`1.5px solid ${tab===t.k ? 'var(--blue)' : 'var(--border)'}`, fontWeight:600 }} onClick={() => setTab(t.k)}>{t.l}</button>
        ))}
      </div>

      {tab === 'gorsel' ? (
        /* HAFTALIK GÖRÜNÜM */
        <div className="card animate-fadeInUp" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
              <thead>
                <tr style={{ background:'linear-gradient(135deg,#F0F4FF,#EEF2FF)' }}>
                  <th style={{ padding:'12px 16px', textAlign:'left', fontSize:'0.7rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-muted)', borderBottom:'2px solid var(--border)', minWidth:160 }}>Öğretmen</th>
                  {GUNLER.map(g => (
                    <th key={g} style={{ padding:'12px 10px', textAlign:'center', fontSize:'0.7rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-muted)', borderBottom:'2px solid var(--border)', minWidth:70 }}>
                      {GUN_KISALT[g]}
                    </th>
                  ))}
                  <th style={{ padding:'12px 10px', textAlign:'center', fontSize:'0.7rem', fontWeight:800, color:'var(--text-muted)', borderBottom:'2px solid var(--border)', minWidth:60 }}>Saat</th>
                  <th style={{ padding:'12px 10px', textAlign:'center', fontSize:'0.7rem', fontWeight:800, color:'var(--text-muted)', borderBottom:'2px solid var(--border)' }}></th>
                </tr>
              </thead>
              <tbody>
                {ogretmenler.map((ogr, idx) => {
                  const color = getBransColor(ogr.brans);
                  const gunluk = GUNLER.map(g => {
                    const m = musaitData.find(md => md.id === ogr.id && md.gun === g);
                    return { gun:g, musait: m ? m.musait===1 : null, notlar: m?.notlar||'' };
                  });
                  const haftalikSaat = musaitData.find(m => m.id===ogr.id && m.haftalik_saat>0)?.haftalik_saat || 0;
                  const tanimliMi = musaitData.some(m => m.id === ogr.id);

                  return (
                    <tr key={ogr.id} style={{ borderBottom:'1px solid var(--border)', transition:'background 0.1s' }} onMouseEnter={e=>e.currentTarget.style.background='#F8FAFF'} onMouseLeave={e=>e.currentTarget.style.background=''}>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:34, height:34, borderRadius:10, background:`${color}18`, color, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'0.85rem', flexShrink:0 }}>
                            {ogr.ad?.[0]}{ogr.soyad?.[0]}
                          </div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:'0.875rem' }}>{ogr.ad} {ogr.soyad}</div>
                            {ogr.brans && <div style={{ fontSize:'0.7rem', color, fontWeight:600 }}>{ogr.brans}</div>}
                          </div>
                        </div>
                      </td>
                      {gunluk.map(g => (
                        <td key={g.gun} style={{ textAlign:'center', padding:'8px' }}>
                          {g.musait === null ? (
                            <span style={{ fontSize:'1rem', opacity:0.3 }}>—</span>
                          ) : g.musait ? (
                            <div title={g.notlar || 'Müsait'} style={{ width:28, height:28, borderRadius:8, background:'#D1FAE5', border:'1.5px solid #10B98150', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.85rem', cursor:'default' }}>✅</div>
                          ) : (
                            <div title={g.notlar || 'Müsait Değil'} style={{ width:28, height:28, borderRadius:8, background:'#FEE2E2', border:'1.5px solid #EF444450', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.85rem', cursor:'default' }}>❌</div>
                          )}
                        </td>
                      ))}
                      <td style={{ textAlign:'center', fontFamily:'var(--font-ui)', fontWeight:700, fontSize:'0.9rem' }}>
                        {haftalikSaat > 0 ? <span style={{ color:'var(--blue)', background:'#EFF6FF', padding:'2px 8px', borderRadius:99, fontSize:'0.78rem' }}>{haftalikSaat}s</span> : <span style={{ color:'var(--text-muted)', fontSize:'0.78rem' }}>—</span>}
                      </td>
                      <td style={{ textAlign:'center', padding:'8px 12px' }}>
                        <button className="btn btn-outline btn-sm" style={{ fontSize:'0.72rem' }} onClick={() => openEdit(ogr)}>
                          {tanimliMi ? '✏️ Düzenle' : '+ Ekle'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* LİSTE GÖRÜNÜMÜ */
        <div style={{ display:'flex', flexDirection:'column', gap:12 }} className="animate-fadeInUp">
          {ogretmenler.map((ogr) => {
            const color = getBransColor(ogr.brans);
            const musaitGunler = musaitData.filter(m => m.id===ogr.id && m.musait===1).map(m => GUN_KISALT[m.gun]);
            const musaitDegil = musaitData.filter(m => m.id===ogr.id && m.musait===0).map(m => GUN_KISALT[m.gun]);
            const haftalikSaat = musaitData.find(m => m.id===ogr.id && m.haftalik_saat>0)?.haftalik_saat || 0;

            return (
              <div key={ogr.id} className="card" style={{ padding:'16px 20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:40, height:40, borderRadius:12, background:`${color}18`, color, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-ui)', fontWeight:900, flexShrink:0 }}>
                      {ogr.ad?.[0]}{ogr.soyad?.[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight:700 }}>{ogr.ad} {ogr.soyad}</div>
                      <div style={{ fontSize:'0.78rem', color, fontWeight:600 }}>{ogr.brans || 'Branş girilmemiş'}</div>
                    </div>
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(ogr)}>✏️ Müsaitlik</button>
                </div>
                {musaitData.some(m => m.id===ogr.id) && (
                  <div style={{ display:'flex', gap:16, marginTop:12, paddingTop:12, borderTop:'1px solid var(--border)', flexWrap:'wrap', fontSize:'0.78rem' }}>
                    {musaitGunler.length > 0 && <div><span style={{ color:'#10B981', fontWeight:700 }}>✅ Müsait: </span>{musaitGunler.join(', ')}</div>}
                    {musaitDegil.length > 0 && <div><span style={{ color:'#EF4444', fontWeight:700 }}>❌ Gelemiyor: </span>{musaitDegil.join(', ')}</div>}
                    {haftalikSaat > 0 && <div><span style={{ color:'var(--blue)', fontWeight:700 }}>⏰ Haftalık: </span>{haftalikSaat} saat</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* DÜZENLEME MODALI */}
      {secOgretmen && (
        <div className="modal-backdrop" onClick={() => setSecOgretmen(null)}>
          <div className="modal" style={{ maxWidth:560 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">📋 {secOgretmen.ad} {secOgretmen.soyad}</h3>
            <p className="modal-subtitle">Müsait günler ve saat kapasitesini ayarlayın</p>

            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {editData.map((g, i) => (
                <div key={g.gun} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:10 }}>
                  <div style={{ width:80, fontWeight:700, fontSize:'0.85rem', color:'var(--text-primary)' }}>{g.gun}</div>
                  <button onClick={() => { const nd = [...editData]; nd[i] = {...g, musait:true}; setEditData(nd); }} style={{ padding:'5px 12px', borderRadius:8, border:'1.5px solid', borderColor: g.musait ? '#10B981' : 'var(--border)', background: g.musait ? '#D1FAE5' : 'white', color: g.musait ? '#065F46' : 'var(--text-muted)', fontSize:'0.78rem', fontWeight:700, cursor:'pointer', transition:'all 0.1s' }}>✅ Müsait</button>
                  <button onClick={() => { const nd = [...editData]; nd[i] = {...g, musait:false}; setEditData(nd); }} style={{ padding:'5px 12px', borderRadius:8, border:'1.5px solid', borderColor: !g.musait ? '#EF4444' : 'var(--border)', background: !g.musait ? '#FEE2E2' : 'white', color: !g.musait ? '#991B1B' : 'var(--text-muted)', fontSize:'0.78rem', fontWeight:700, cursor:'pointer', transition:'all 0.1s' }}>❌ Gelemiyor</button>
                  <input type="text" style={{ flex:1, padding:'5px 10px', borderRadius:8, border:'1.5px solid var(--border)', fontSize:'0.78rem', background:'white' }} placeholder="Not (geç gelir, vs.)" value={g.notlar} onChange={e => { const nd=[...editData]; nd[i]={...g,notlar:e.target.value}; setEditData(nd); }} />
                </div>
              ))}

              <div className="form-group" style={{ margin:'8px 0 0' }}>
                <label className="form-label">Haftalık Toplam Saat Kapasitesi</label>
                <input type="number" className="form-input" min="0" max="50" placeholder="Örn: 20" 
                  value={editData[0]?.haftalik_saat || ''} 
                  onChange={e => setEditData(prev => prev.map(g => ({...g, haftalik_saat: parseInt(e.target.value)||0})))} />
              </div>

              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button className="btn btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={handleSave} disabled={saving}>
                  {saving ? <><div className="spinner" style={{width:16,height:16}} /> Kaydediliyor...</> : '💾 Kaydet'}
                </button>
                <button className="btn btn-outline" onClick={() => setSecOgretmen(null)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
